<!--
  DOWNLOADS — everything this device is holding, and what each one is doing.

  Reached from the Downloads card on Profile. ONE ROW PER THING, not per file:
  a series is a single row that opens to its episodes, a film is a row that does
  not open. The owner, looking at eight rows of Silo: "we don't need two
  different tiles for the same series - it should be one that opens up the list
  of downloaded episodes." `groupDownloads` in index-state.ts is that rule, and
  it is the same one Continue watching applies.

  Each row carries the one control it can use right now: a progress ring while
  it works, Play when it is finished, a bin either way. On a series row the ring
  follows the episode being fetched and the bin takes the whole show.

  THE ROW IS THE CONTROL, NOT A MENU. Netflix puts the action on the row and the
  destructive one behind a press; so does this. "Delete all" lives behind the ⋯
  in the header because it is the only thing here that can lose a whole evening's
  download in one press, and it should not sit under a thumb that was aiming at
  a progress ring.

  THE HONEST SENTENCE ABOUT iOS is at the bottom of the list and it is not
  decoration: WebKit evicts a site's whole origin storage after 7 days without
  interaction unless the site is on the Home Screen, and an iOS web app gets
  roughly 1 GB per origin. Neither is detectable after the fact — the bytes are
  simply gone — so the screen says so rather than letting somebody discover it
  on a plane. See `lib/offline/types.ts`.
-->
<script lang="ts">
  import Art from './Art.svelte';
  import Button from './Button.svelte';
  import Empty from './Empty.svelte';
  import Icon from './Icon.svelte';
  import Sheet from './Sheet.svelte';
  import { groupDownloads, groupStatus, percentOf, sizeLabel, totalBytes } from '$lib/offline/index-state';
  import {
    bootDownloads,
    downloads,
    remove,
    removeEverything,
    toggle
  } from '$lib/offline/manager.svelte';
  import * as destination from '$lib/offline/destination';
  import { posterUrlFor, supported } from '$lib/offline/store';
  import type { DownloadEntry } from '$lib/offline/types';
  import { posterUrl } from './cinemeta';
  import { clock } from './format';

  interface Props {
    /** Drawn as a sub-screen under Profile, so the caller owns the way back. */
    onback?: (() => void) | undefined;
  }
  let { onback }: Props = $props();

  $effect(() => {
    void bootDownloads();
  });

  const items = $derived(downloads.items);
  const groups = $derived(groupDownloads(items));
  const held = $derived(totalBytes(items));
  const finished = $derived(items.filter((e) => e.state === 'done').length);

  /* Which series rows are open. Keyed by group key, so a re-render while an
     episode finishes does not collapse the list under the reader's thumb. */
  let opened = $state<Record<string, boolean>>({});

  /*
   * POSTERS FROM THE DISK FIRST. Each group asks OPFS for the poster saved
   * beside its first episode and gets a blob URL; the network URL is only the
   * fallback. Offline the network URL is nothing, and the row used to draw
   * its initial - the owner: "there are no assets for the tile."
   */
  let posters = $state<Record<string, string>>({});
  $effect(() => {
    for (const group of groups) {
      const key = group.entries[0].key;
      if (posters[key] !== undefined) continue;
      posters = { ...posters, [key]: '' };
      void posterUrlFor(key).then((url) => {
        if (url) posters = { ...posters, [key]: url };
      });
    }
  });
  function toggleOpen(key: string): void {
    opened = { ...opened, [key]: !opened[key] };
  }
  /** Delete a whole group: the series row's bin means the show, not one file. */
  async function removeGroup(keys: string[]): Promise<void> {
    for (const key of keys) await remove(key);
  }

  let confirmingAll = $state(false);
  let menuOpen = $state(false);

  function rowLabel(entry: DownloadEntry): string {
    return entry.episodeLabel ? `${entry.title} · ${entry.episodeLabel}` : entry.title;
  }

  /**
   * What the row says under its name. One line, and it answers "where is it".
   *
   * This screen is a HISTORY now, not a library. The owner: "the download
   * section just shows a history of downloads. It doesn't actually let you
   * play them. You have to go find them within your Files." So a finished row
   * names the file and the folder it went to, because that is what the reader
   * needs in order to find it.
   */
  function statusLine(entry: DownloadEntry): string {
    if (entry.state === 'done') {
      const where = entry.savedVia === 'folder'
        ? entry.savedPath ? `Saved to ${entry.savedPath}` : 'Saved to your downloads folder'
        : 'Saved to your device';
      return `${where} · ${sizeLabel(entry.size)}`;
    }
    if (entry.state === 'failed') return entry.error ?? 'Stopped.';
    if (entry.state === 'downloading') {
      /*
       * WHILE IT IS CONVERTING, COUNT MINUTES OF FILM — NOT MEGABYTES.
       *
       * A .mkv is rewritten to MP4 on the device as it arrives (lib/offline/
       * remux.ts), and during that the two numbers a byte copy had stop
       * meaning what they say: `bytes` is how much of the SOURCE has been
       * read, and the file appearing on the device is a different size that
       * nobody knows yet. "312 MB of 766 MB" would be describing a file the
       * reader is not getting. "12:34 of 51:20" is the thing that is actually
       * happening, and it is the number that moves steadily.
       *
       * The ring above is unchanged and still follows `bytes`: as a FRACTION
       * the source's progress is exactly right, and it is the same fraction
       * either way.
       */
      if (entry.convertedSec !== undefined && entry.durationSec) {
        return `Converting for your device · ${clock(entry.convertedSec)} of ${clock(entry.durationSec)}`;
      }
      if (entry.convertedSec !== undefined) {
        return `Converting for your device · ${clock(entry.convertedSec)}`;
      }
      return entry.size > 0
        ? `${sizeLabel(entry.bytes)} of ${sizeLabel(entry.size)}`
        : 'Finding the file…';
    }
    return entry.bytes > 0 ? `Paused · ${sizeLabel(entry.bytes)} so far` : 'Waiting';
  }

  /*
   * THE DESTINATION PICKER. See destination.ts: `showDirectoryPicker` exists
   * on Chrome and Edge on the desktop and nowhere else, so this whole block is
   * absent on an iPhone rather than offered and then failing.
   */
  let canPickFolder = $state(false);
  let folderName = $state('');
  $effect(() => {
    canPickFolder = destination.strategy() === 'folder';
    if (!canPickFolder) return;
    void destination.currentFolder().then((handle) => {
      folderName = handle?.name ?? '';
    });
  });
  async function choose(): Promise<void> {
    const handle = await destination.pickFolder();
    if (handle) folderName = handle.name;
  }
</script>

<div class="screen">
  <header class="head">
    {#if onback}
      <button type="button" class="back" onclick={onback} aria-label="Back to your profile">
        <Icon name="back" size={20} />
      </button>
    {/if}
    <div class="titling">
      <h1>Downloads</h1>
      <p>
        {#if items.length === 0}Nothing on this device yet{:else}{finished} ready · {sizeLabel(held)} used{/if}
      </p>
    </div>
    {#if items.length > 0}
      <button type="button" class="more" aria-haspopup="dialog" aria-expanded={menuOpen} onclick={() => (menuOpen = true)} aria-label="Download options">
        <Icon name="dots" size={20} />
      </button>
    {/if}
  </header>

  {#if canPickFolder}
    <!-- THE DESTINATION. Only Chrome and Edge on the desktop can offer this
         (destination.ts explains why); everywhere else the browser's own
         download manager decides, and there is nothing useful to show. -->
    <div class="destination">
      <div>
        <strong>Where downloads go</strong>
        <p>{folderName ? `Saving into “${folderName}”. Series get their own folders.` : 'Pick a folder once and every download lands there.'}</p>
      </div>
      <button type="button" onclick={choose}>{folderName ? 'Change' : 'Choose folder'}</button>
    </div>
  {/if}

  {#if !supported()}
    <!-- Not an error and not this app's fault: an older WebKit, or a browser
         mode that refuses storage entirely. Say which thing is missing. -->
    <Empty
      icon="alert"
      title="This browser cannot keep downloads"
      detail="Jovii keeps downloads in the browser's own private file store, and this browser does not offer one. Everything else still works."
    />
  {:else if items.length === 0}
    <Empty
      icon="down"
      title="Nothing downloaded"
      detail="Press Download on a film, or Download Season on a series, and it will be here to watch with the wifi off."
    />
  {:else}
    <ul class="list">
      {#each groups as group (group.key)}
        {@const single = group.entries[0]}
        {@const series = group.kind === 'series'}
        {@const lead = group.active ?? group.entries[0]}
        {@const open = opened[group.key] === true}
        <li class:failed={!series && single.state === 'failed'}>
          <div class="art">
            <Art
              src={posters[group.entries[0].key] || group.poster || posterUrl(group.entries[0].ref.imdbId, 'small')}
              alt=""
              label={group.title}
              ratio="2 / 3"
            />
          </div>

          <div class="text">
            {#if series}
              <!-- The whole row opens the list; it is a button, not a link,
                   because it reveals rather than navigates. -->
              <button
                type="button"
                class="opener"
                aria-expanded={open}
                onclick={() => toggleOpen(group.key)}
              >
                <span class="name">{group.title}</span>
                <Icon name={open ? 'up' : 'down'} size={15} />
              </button>
              <p class="status">{groupStatus(group)}</p>
            {:else}
              <p class="name">{group.title}</p>
              <p class="status">{statusLine(single)}</p>
            {/if}
            {#if lead && (lead.state === 'downloading' || (lead.state === 'queued' && lead.bytes > 0))}
              <progress max="100" value={percentOf(lead)} aria-label={`${group.title} download progress`}></progress>
            {/if}
          </div>

          <div class="acts">
            {#if !series && single.state !== 'done'}
              <button
                type="button"
                class="act ring"
                style="--pct: {percentOf(single)}"
                onclick={() => toggle(single.key)}
                aria-label={single.state === 'failed' ? `Try ${group.title} again` : `Stop downloading ${group.title}`}
              >
                <Icon name={single.state === 'failed' ? 'refresh' : 'close'} size={15} />
              </button>
            {:else if group.active}
              {@const busy = group.active}
              <button
                type="button"
                class="act ring"
                style="--pct: {percentOf(busy)}"
                onclick={() => toggle(busy.key)}
                aria-label={`Stop downloading ${busy.episodeLabel ?? group.title}`}
              >
                <Icon name="close" size={15} />
              </button>
            {/if}
            <button
              type="button"
              class="act bin"
              onclick={() => void removeGroup(group.entries.map((e) => e.key))}
              aria-label={series
                ? `Delete all ${group.entries.length} downloaded episodes of ${group.title}`
                : `Delete ${group.title} from this device`}
            >
              <Icon name="trash" size={17} />
            </button>
          </div>
        </li>

        {#if series && open}
          {#each group.entries as entry (entry.key)}
            {@const pct = percentOf(entry)}
            <li class="child" class:failed={entry.state === 'failed'}>
              <div class="text">
                <p class="name">{entry.episodeLabel ?? entry.title}</p>
                <p class="status">{statusLine(entry)}</p>
                {#if entry.state === 'downloading' || (entry.state === 'queued' && entry.bytes > 0)}
                  <progress max="100" value={pct} aria-label={`${rowLabel(entry)} download progress`}></progress>
                {/if}
              </div>
              <div class="acts">
                {#if entry.state !== 'done'}
                  <button
                    type="button"
                    class="act ring"
                    style="--pct: {pct}"
                    onclick={() => toggle(entry.key)}
                    aria-label={entry.state === 'failed' ? `Try ${rowLabel(entry)} again` : `Stop downloading ${rowLabel(entry)}`}
                  >
                    <Icon name={entry.state === 'failed' ? 'refresh' : 'close'} size={15} />
                  </button>
                {/if}
                <button
                  type="button"
                  class="act bin"
                  onclick={() => void remove(entry.key)}
                  aria-label={`Delete ${rowLabel(entry)} from this device`}
                >
                  <Icon name="trash" size={17} />
                </button>
              </div>
            </li>
          {/each}
        {/if}
      {/each}
    </ul>

    <p class="keep">
      <Icon name="info" size={14} />
      <span>
        Your films are saved as real files on this device — open them from Files,
        Finder or your downloads folder, in any player you like. This list is the
        history; deleting a row here does not delete the file.
      </span>
    </p>
  {/if}
</div>

{#if menuOpen}
  <Sheet open title="Downloads" caption={`${items.length} on this device · ${sizeLabel(held)}`} onclose={() => (menuOpen = false)}>
    <div class="menu">
      <button
        type="button"
        class="menu-row danger"
        onclick={() => {
          menuOpen = false;
          confirmingAll = true;
        }}
      >
        <Icon name="trash" size={17} />
        <span>Delete all downloads</span>
      </button>
    </div>
  </Sheet>
{/if}

{#if confirmingAll}
  <Sheet open title="Delete every download?" onclose={() => (confirmingAll = false)}>
    <p class="confirm">
      This removes <strong>{sizeLabel(held)}</strong> from this device. Nothing else changes:
      your list, your place in each film and your history all stay.
    </p>
    {#snippet footer()}
      <div class="confirm-acts">
        <Button onclick={() => (confirmingAll = false)}>Cancel</Button>
        <button
          type="button"
          class="destroy"
          onclick={() => {
            confirmingAll = false;
            void removeEverything();
          }}
        >
          Delete all
        </button>
      </div>
    {/snippet}
  </Sheet>
{/if}

<style>
  .screen {
    padding-bottom: var(--space-6);
  }

  .head {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-5) max(var(--page-pad), var(--safe-r)) var(--space-3)
      max(var(--page-pad), var(--safe-l));
  }
  .titling {
    flex: 1;
    min-width: 0;
  }
  h1 {
    font-size: var(--text-2xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: 1.15;
  }
  .head p {
    margin-top: 2px;
    color: var(--text-faint);
    font-size: var(--text-xs);
  }
  .back,
  .more {
    display: grid;
    place-content: center;
    flex: none;
    width: var(--hit);
    height: var(--hit);
    border-radius: var(--radius-pill);
    color: var(--text-dim);
  }
  .back {
    margin-left: calc(var(--space-3) * -1);
  }
  .back:hover,
  .more:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .back:focus-visible,
  .more:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -3px;
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) max(var(--page-pad), var(--safe-r)) var(--space-3)
      max(var(--page-pad), var(--safe-l));
    border-top: 1px solid color-mix(in oklab, var(--line) 60%, transparent);
  }
  li:first-child {
    border-top: 0;
  }
  .art {
    border-radius: var(--radius-xs);
    overflow: hidden;
    background: var(--surface-2);
  }

  .text {
    min-width: 0;
  }
  .name {
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status {
    margin-top: 2px;
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: 1.35;
  }
  li.failed .status {
    color: var(--bad);
  }
  progress {
    display: block;
    width: 100%;
    height: 3px;
    margin-top: var(--space-2);
    accent-color: var(--accent);
  }

  .acts {
    display: flex;
    align-items: center;
    gap: 2px;
  }
  .act {
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    flex: none;
    border-radius: var(--radius-pill);
    color: var(--text-dim);
  }
  .act:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .act:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -3px;
  }

  /*
   * THE PROGRESS RING. A conic gradient inside a masked disc, which is the whole
   * of it: no SVG, no stroke-dasharray arithmetic, and it repaints by changing
   * one custom property. The mask cuts the middle out so the glyph sits in a
   * hole rather than on a filled circle.
   */
  .ring {
    position: relative;
  }
  .ring::before {
    content: '';
    position: absolute;
    /* Centred on the button. An absolutely positioned pseudo with no offsets
       sits at the static position (top-left of the content box), which is
       where the ring was on the owner's phone - off to the upper left of the
       glyph. inset + margin:auto is the one centring that needs no size maths. */
    inset: 0;
    margin: auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: conic-gradient(var(--accent) calc(var(--pct, 0) * 1%), color-mix(in oklab, var(--text-faint) 34%, transparent) 0);
    -webkit-mask: radial-gradient(circle, transparent 10px, #000 10.5px);
    mask: radial-gradient(circle, transparent 10px, #000 10.5px);
  }
  .ring :global(svg) {
    position: relative;
  }

  /* The series row's name is a button that opens the episode list. It carries
     no pill and no colour of its own: it is the row's title that happens to
     also be the control, which is why the chevron sits beside it. */
  .opener {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    color: inherit;
    text-align: left;
    min-height: var(--hit);
    margin: calc((var(--hit) - 1.2em) / -2) 0;
  }
  .opener:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
    border-radius: 6px;
  }

  /* An episode under its series. Indented to where the artwork ends, so the
     list reads as one thing with parts rather than two lists interleaved. */
  li.child {
    grid-template-columns: 1fr auto;
    padding-left: calc(var(--page-pad) + 52px + var(--space-3));
    background: color-mix(in oklab, var(--surface-1) 40%, transparent);
  }
  li.child .name {
    font-weight: var(--weight-regular);
    font-size: var(--text-sm);
  }

  .destination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    margin: 0 max(var(--page-pad), var(--safe-l)) var(--space-4);
    padding: var(--space-4);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface-1);
  }
  .destination p {
    margin-top: 3px;
    color: var(--text-dim);
    font-size: var(--text-sm);
  }
  .destination button {
    flex: none;
    min-height: var(--hit);
    padding-inline: var(--space-4);
    border-radius: var(--radius-pill);
    background: var(--surface-3);
    color: var(--text);
    font-weight: var(--weight-semi);
  }

  .keep {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin-top: var(--space-4);
    padding: 0 max(var(--page-pad), var(--safe-r)) 0 max(var(--page-pad), var(--safe-l));
    color: var(--text-faint);
    font-size: var(--text-xs);
    line-height: var(--leading);
    text-wrap: pretty;
  }
  .keep :global(svg) {
    flex: none;
    margin-top: 2px;
  }

  .menu {
    padding: var(--space-2) var(--space-4);
  }
  .menu-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: var(--hit);
    padding: 0 var(--space-2);
    border-radius: var(--radius);
    color: var(--text);
    font-size: var(--text-md);
    text-align: start;
  }
  .menu-row:hover {
    background: var(--surface-2);
  }
  .menu-row.danger {
    color: var(--bad);
  }

  .confirm {
    padding: var(--space-3) var(--space-4);
    color: var(--text-dim);
    line-height: var(--leading);
    text-wrap: pretty;
  }
  .confirm-acts {
    display: flex;
    gap: var(--space-3);
  }
  .destroy {
    min-height: var(--hit);
    padding: var(--space-2) var(--space-4);
    border: 1px solid var(--bad);
    border-radius: var(--radius-pill);
    color: var(--bad);
    touch-action: manipulation;
  }
  .destroy:hover {
    background: var(--surface-2);
  }
</style>